import React, { useEffect, useState } from 'react';
import { Button, Input, message as antdMessage } from 'antd';
import './ContactForm.css';

// PROD site key you gave me
const PROD_SITE_KEY = '6LfqlqArAAAAACiGP7zSB6xfWQmlVJZFT9y_6cOs';

// If you made a dev key, put it here; otherwise we’ll just use prod on localhost
const DEV_SITE_KEY = PROD_SITE_KEY;

const HOST = window.location.hostname;
const RECAPTCHA_SITE_KEY =
  HOST === 'localhost' || HOST === '127.0.0.1' ? DEV_SITE_KEY : PROD_SITE_KEY;

const RECAPTCHA_SCRIPT_ID = 'recaptcha-v3-script';
const RECAPTCHA_ACTION = 'contact';

// 👉 NEW: use the full Cloud Function URL only when running locally
const isLocalHost = HOST === 'localhost' || HOST === '127.0.0.1';
const CF_URL = 'https://us-central1-black-canyon-tickets-bct.cloudfunctions.net/sendContactMessage';

// OPTIONAL: helps catch anonymous rejections in dev
if (process.env.NODE_ENV !== 'production' && !window.__addedUnhandledRejectionLogger) {
  window.addEventListener('unhandledrejection', (e) => {
    // eslint-disable-next-line no-console
    console.error('Unhandled promise rejection:', e.reason || e);
  });
  window.__addedUnhandledRejectionLogger = true;
}

function ContactForm() {
  const [email, setEmail] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);

  // Load reCAPTCHA v3 script with onload/onerror + timeout
  useEffect(() => {
    if (document.getElementById(RECAPTCHA_SCRIPT_ID)) return;

    const s = document.createElement('script');
    s.id = RECAPTCHA_SCRIPT_ID;
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;

    const onLoad = () => {
      // eslint-disable-next-line no-console
      console.log('[reCAPTCHA] script loaded');
    };
    const onError = () => {
      // eslint-disable-next-line no-console
      console.error('[reCAPTCHA] script failed to load');
      antdMessage.error('reCAPTCHA failed to load. Please refresh and try again.');
    };

    s.addEventListener('load', onLoad);
    s.addEventListener('error', onError);
    document.body.appendChild(s);

    // Safety timeout: if grecaptcha never appears
    const t = setTimeout(() => {
      if (!window.grecaptcha) {
        // eslint-disable-next-line no-console
        console.error('[reCAPTCHA] grecaptcha not available after timeout');
      }
    }, 4000);

    return () => {
      clearTimeout(t);
      s.removeEventListener('load', onLoad);
      s.removeEventListener('error', onError);
    };
  }, []);

  // Robust token getter with explicit errors
  const getRecaptchaToken = async () => {
    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA not loaded. Is the Site Key allowed for this domain?');
    }
    return new Promise((resolve, reject) => {
      try {
        window.grecaptcha.ready(() => {
          try {
            const p = window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: RECAPTCHA_ACTION });
            if (!p || typeof p.then !== 'function') {
              reject(new Error('reCAPTCHA execute did not return a promise'));
              return;
            }
            p.then(resolve).catch((err) => {
              reject(err instanceof Error ? err : new Error(String(err || 'reCAPTCHA execute failed')));
            });
          } catch (err) {
            reject(err instanceof Error ? err : new Error(String(err || 'reCAPTCHA execute threw')));
          }
        });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err || 'reCAPTCHA ready threw')));
      }
    });
  };

  // Safe JSON parse that also shows HTML mistakes
  const parseJsonSafe = async (res) => {
    const ct = res.headers.get('content-type') || '';
    const text = await res.text();
    if (!ct.includes('application/json')) {
      // eslint-disable-next-line no-console
      console.warn('[send-contact] Non-JSON response. Status:', res.status, 'Body:', text.slice(0, 500));
      throw new Error('Server did not return JSON (check Hosting rewrites / function URL)');
    }
    try {
      return JSON.parse(text);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[send-contact] JSON parse failed. Status:', res.status, 'Body:', text.slice(0, 500));
      throw new Error('Invalid JSON in response');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !body) return;

    if (body.length < 5) {
      antdMessage.error('Message too short.');
      return;
    }
    if (body.length > 2000) {
      antdMessage.error('Message too long (max 2000 chars).');
      return;
    }

    try {
      setLoading(true);

      // 1) Get reCAPTCHA token
      const recaptchaToken = await getRecaptchaToken();
      // eslint-disable-next-line no-console
      console.log('[reCAPTCHA] token acquired (length):', (recaptchaToken || '').length);

      // 2) Endpoint selection:
      //    - on localhost: call the Cloud Function URL directly
      //    - on prod: use the clean rewrite path
      const endpoint = isLocalHost ? CF_URL : '/api/send-contact';

      const payload = { email, message: body, recaptchaToken, action: RECAPTCHA_ACTION };

      // eslint-disable-next-line no-console
      console.log('[send-contact] POST', endpoint, payload);

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await parseJsonSafe(res);

      if (res.ok && data?.success) {
  alert('Message sent successfully!');           // ← add this line
  antdMessage.success('Message sent! We’ll get back to you shortly.');
  setEmail('');
  setBody('');
} else { 
        throw new Error(data?.error || `Request failed (${res.status})`);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[send-contact] error:', err);
      antdMessage.error(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-form-container">
      <h2 className="contact-heading" data-aos="fade-up">Get In Touch</h2>
      <p className="contact-subheading" data-aos="fade-up" data-aos-delay="50">Have a question or need help? We'd love to hear from you.</p>
      <form onSubmit={handleSubmit} className="contact-form">
        <Input
          type="email"
          placeholder="Your Email"
          className="contact-input"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
        <Input.TextArea
          placeholder="Your Message"
          rows={5}
          className="contact-textarea"
          required
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={2000}
          showCount
        />
        <Button
          type="primary"
          htmlType="submit"
          className="contact-button"
          loading={loading}
          disabled={loading}
        >
          Send
        </Button>
      </form>
    </div>
  );
}

export default ContactForm;
